import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

import { EASE_OUT, NOPING_GREEN, PURPLE } from '../constants';

export function TournamentBracket() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div ref={ref} initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : { opacity: 0 }} transition={{ duration: 1, ease: EASE_OUT }} className='relative mx-auto h-[300px] w-full max-w-[600px] md:h-[360px]' aria-hidden='true'>
      {/* Left bracket lines */}
      <div className='absolute start-[8%] top-[10%] h-[30%] w-px bg-gradient-to-b from-[#93FF18]/60 to-[#93FF18]/20' />
      <div className='absolute start-[8%] top-[60%] h-[30%] w-px bg-gradient-to-b from-[#93FF18]/20 to-[#93FF18]/60' />
      <div className='absolute start-[8%] top-1/4 h-px w-[12%] bg-[#93FF18]/40' />
      <div className='absolute start-[8%] top-3/4 h-px w-[12%] bg-[#93FF18]/40' />
      <div className='absolute start-[20%] top-1/4 h-[50%] w-px bg-gradient-to-b from-[#93FF18]/40 via-[#93FF18]/60 to-[#93FF18]/40' />
      <div className='absolute start-[20%] top-[50%] h-px w-[15%] bg-[#93FF18]/50' />

      {/* Right bracket lines */}
      <div className='absolute end-[8%] top-[10%] h-[30%] w-px bg-gradient-to-b from-[#93FF18]/60 to-[#93FF18]/20' />
      <div className='absolute end-[8%] top-[60%] h-[30%] w-px bg-gradient-to-b from-[#93FF18]/20 to-[#93FF18]/60' />
      <div className='absolute top-1/4 h-px w-[12%] bg-[#93FF18]/40' style={{ right: '8%' }} />
      <div className='absolute top-3/4 h-px w-[12%] bg-[#93FF18]/40' style={{ right: '8%' }} />
      <div className='absolute end-[20%] top-1/4 h-[50%] w-px bg-gradient-to-b from-[#93FF18]/40 via-[#93FF18]/60 to-[#93FF18]/40' />
      <div className='absolute top-[50%] h-px w-[15%] bg-[#93FF18]/50' style={{ right: '20%' }} />

      {/* Center connector */}
      <div className='absolute left-[35%] top-[50%] h-px w-[30%] bg-gradient-to-r from-[#93FF18]/50 via-[#93FF18] to-[#93FF18]/50' />

      {/* Match nodes - left */}
      {[
        { x: '5%', y: '8%' },
        { x: '5%', y: '37%' },
        { x: '5%', y: '58%' },
        { x: '5%', y: '85%' },
      ].map((pos, i) => (
        <div key={`left-${i}`} className='absolute size-3 rounded-full border-2 border-[#93FF18]/50' style={{ left: pos.x, top: pos.y, backgroundColor: PURPLE }} />
      ))}

      {/* Match nodes - right */}
      {[
        { x: '95%', y: '8%' },
        { x: '95%', y: '37%' },
        { x: '95%', y: '58%' },
        { x: '95%', y: '85%' },
      ].map((pos, i) => (
        <div key={`right-${i}`} className='absolute size-3 -translate-x-full rounded-full border-2 border-[#93FF18]/50' style={{ left: pos.x, top: pos.y, backgroundColor: PURPLE }} />
      ))}

      {/* Semi-final nodes */}
      <div className='absolute start-[18%] top-[23%] size-4 rounded-full border-2 border-[#93FF18]/70' style={{ backgroundColor: PURPLE }} />
      <div className='absolute start-[18%] top-[73%] size-4 rounded-full border-2 border-[#93FF18]/70' style={{ backgroundColor: PURPLE }} />
      <div className='absolute end-[18%] top-[23%] size-4 rounded-full border-2 border-[#93FF18]/70' style={{ backgroundColor: PURPLE }} />
      <div className='absolute end-[18%] top-[73%] size-4 rounded-full border-2 border-[#93FF18]/70' style={{ backgroundColor: PURPLE }} />

      {/* Center trophy glow */}
      <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'>
        <div className='flex size-20 items-center justify-center rounded-full border md:size-24' style={{ backgroundColor: `${NOPING_GREEN}10`, borderColor: `${NOPING_GREEN}30` }}>
          <div className='flex size-14 items-center justify-center rounded-full md:size-16' style={{ backgroundColor: `${NOPING_GREEN}15` }}>
            <span className='text-3xl md:text-4xl'>&#x1F3C6;</span>
          </div>
        </div>
      </div>

      {/* Decorative horizontal fades */}
      <div className='absolute left-0 top-[15%] h-px w-full bg-gradient-to-r from-transparent via-[#93FF18]/10 to-transparent' />
      <div className='absolute left-0 top-[85%] h-px w-full bg-gradient-to-r from-transparent via-[#93FF18]/10 to-transparent' />
    </motion.div>
  );
}
